import { f7 } from 'framework7-vue';
import { onScopeDispose, ref } from 'vue';

/**
 * Composable for Framework7 toast notifications
 * Provides a clean API for showing toasts with automatic cleanup
 * Follows Framework7 documentation patterns
 *
 * @returns {Object} Toast utilities
 */
export function useToast() {
  // Track all toast instances for proper cleanup
  const toastInstances = ref(new Map());

  /**
   * Show a toast notification
   * @param {string|Object} options - Toast text or configuration object
   * @param {string} options.text - Toast message text
   * @param {string} options.position - Toast position: 'top', 'center', 'bottom' (default: 'center')
   * @param {string} options.horizontalPosition - Horizontal alignment: 'left', 'center', 'right' (default: 'left')
   * @param {number} options.closeTimeout - Auto-close timeout in ms (default: 3000)
   * @param {boolean} options.closeButton - Show close button (default: false)
   * @param {string} options.closeButtonText - Custom close button text
   * @param {string} options.closeButtonColor - Close button color
   * @param {string} options.icon - Toast icon HTML
   * @param {string} options.cssClass - Additional CSS class
   * @param {boolean} options.destroyOnClose - Destroy toast on close (default: true)
   * @param {Function} options.on.open - Callback when toast opens
   * @param {Function} options.on.opened - Callback when toast fully opened
   * @param {Function} options.on.close - Callback when toast closes
   * @param {Function} options.on.closed - Callback when toast fully closed
   * @returns {Object} Toast instance with open, close, and destroy methods
   */
  const showToast = (options) => {
    // Handle simple string parameter
    if (typeof options === 'string') {
      options = { text: options };
    }

    // Set defaults following Framework7 conventions
    const toastConfig = {
      text: options.text || '',
      position: options.position || 'center',
      horizontalPosition: options.horizontalPosition || 'left',
      closeTimeout: options.closeTimeout !== undefined ? options.closeTimeout : 3000,
      closeButton: options.closeButton || false,
      closeButtonText: options.closeButtonText,
      closeButtonColor: options.closeButtonColor,
      icon: options.icon,
      cssClass: options.cssClass,
      destroyOnClose: options.destroyOnClose !== undefined ? options.destroyOnClose : true,
      on: {
        opened: (toast) => {
          // Track the instance
          const id = generateToastId();
          toastInstances.value.set(id, toast);

          // F7's toast markup carries zero ARIA attributes (WCAG 4.1.3 Status
          // Messages) — every toast shown through this composable is, by
          // definition, a transient status message, so apply this unconditionally
          // rather than as an opt-in per call site.
          if (toast?.el) {
            toast.el.setAttribute('role', 'status');
            toast.el.setAttribute('aria-live', 'polite');
            toast.el.setAttribute('aria-atomic', 'true');
          }

          // Call user's opened callback if provided
          if (options.on?.opened) {
            options.on.opened(toast);
          }
        },
        closed: (toast) => {
          // Remove from tracking when closed
          for (const [id, instance] of toastInstances.value.entries()) {
            if (instance === toast) {
              toastInstances.value.delete(id);
              break;
            }
          }

          // Call user's closed callback if provided
          if (options.on?.closed) {
            options.on.closed(toast);
          }
        },
        ...(options.on?.open && { open: options.on.open }),
        ...(options.on?.close && { close: options.on.close }),
      },
    };

    // Create toast instance
    const toast = f7.toast.create(toastConfig);

    // Open immediately
    toast.open();

    return toast;
  };

  /**
   * Show a success toast (green with checkmark icon)
   * @param {string} text - Success message
   * @param {Object} options - Additional toast options
   */
  const showSuccess = (text, options = {}) => {
    return showToast({
      text,
      icon: '<i class="f7-icons">checkmark_circle_fill</i>',
      cssClass: 'toast-success',
      ...options,
    });
  };

  /**
   * Show an error toast (red with error icon)
   * @param {string} text - Error message
   * @param {Object} options - Additional toast options
   */
  const showError = (text, options = {}) => {
    return showToast({
      text,
      icon: '<i class="f7-icons">exclamationmark_circle_fill</i>',
      cssClass: 'toast-error',
      closeTimeout: 4000, // Longer for errors
      ...options,
    });
  };

  /**
   * Show an info toast (blue with info icon)
   * @param {string} text - Info message
   * @param {Object} options - Additional toast options
   */
  const showInfo = (text, options = {}) => {
    return showToast({
      text,
      icon: '<i class="f7-icons">info_circle_fill</i>',
      cssClass: 'toast-info',
      ...options,
    });
  };

  /**
   * Show a warning toast (orange with warning icon)
   * @param {string} text - Warning message
   * @param {Object} options - Additional toast options
   */
  const showWarning = (text, options = {}) => {
    return showToast({
      text,
      icon: '<i class="f7-icons">exclamationmark_triangle_fill</i>',
      cssClass: 'toast-warning',
      ...options,
    });
  };

  /**
   * Close all active toasts
   */
  const closeAll = () => {
    toastInstances.value.forEach((toast) => {
      if (toast && typeof toast.close === 'function') {
        toast.close();
      }
    });
    toastInstances.value.clear();
  };

  /**
   * Destroy all toast instances (cleanup)
   */
  const destroyAll = () => {
    toastInstances.value.forEach((toast) => {
      if (toast && typeof toast.destroy === 'function') {
        toast.destroy();
      }
    });
    toastInstances.value.clear();
  };

  // Helper to generate unique toast IDs
  let toastIdCounter = 0;
  const generateToastId = () => {
    return `toast_${Date.now()}_${++toastIdCounter}`;
  };

  // Cleanup when the consuming scope tears down. onScopeDispose fires
  // for both component unmount and nested-composable scope disposal,
  // unlike onBeforeUnmount which can miss F7 page-remove timing.
  onScopeDispose(() => {
    destroyAll();
  });

  return {
    showToast,
    showSuccess,
    showError,
    showInfo,
    showWarning,
    closeAll,
    destroyAll,
  };
}
